import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { datadogLogs } from "@datadog/browser-logs";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { IS_CAPACITOR, MOBILE_OAUTH_CALLBACK_URL, SERVICE_URL } from "@src/env";
import { navigationV2 } from "@src/shared/navigation/v2";
import { toErrorMessage } from "@src/shared/utils/errors";

import { setBearerAuthToken } from "../../data/auth.transport";

/**
 * Okta SSO in the Capacitor wrapper, system-browser handoff (mobile spike).
 *
 * Okta's hosted login can't complete inside the app's own WebView, so this
 * opens it in the system browser instead and waits for the OS to hand
 * control back via a custom URL scheme deep link. See
 * apps/mobile/README.md and apps/server's mobile-oauth-handoff plugin for
 * why the deep link carries a one-time exchange code rather than the bearer
 * token itself.
 *
 * The server's `/mobile-oauth-handoff/start` endpoint (not
 * `authClient.signIn.oauth2` directly) is opened here on purpose: the
 * WebView and the system browser are separate cookie jars, so if the sign-in
 * call happened in the WebView, the OAuth state cookie it sets would never
 * be presentable when Okta's callback lands in the system browser —
 * better-auth would reject it as `state_mismatch`. Opening the start
 * endpoint directly means sign-in, Okta's redirect, and the callback all
 * happen inside the one system-browser session.
 */
export async function startMobileOktaLogin(): Promise<void> {
  await Browser.open({
    url: `${SERVICE_URL}/api/auth/mobile-oauth-handoff/start`,
  });
}

async function exchangeHandoffCode(code: string): Promise<void> {
  const response = await fetch(
    `${SERVICE_URL}/api/auth/mobile-oauth-handoff/exchange`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    },
  );

  if (!response.ok) {
    let serverMessage = "Mobile OAuth handoff code exchange failed";
    try {
      const body = (await response.json()) as { message?: string };
      if (body?.message) serverMessage = body.message;
    } catch {
      // Keep the default message if the error body isn't JSON.
    }
    throw new Error(serverMessage);
  }

  const { token } = (await response.json()) as { token?: string };

  if (!token) {
    throw new Error("Mobile OAuth handoff response missing token");
  }

  setBearerAuthToken(token);
}

/**
 * Mount once near the app root (gated by IS_CAPACITOR) to catch the deep
 * link the system browser hands back after Okta login completes.
 *
 * The callback URL arrives two different ways depending on app state:
 * `appUrlOpen` when the app is already running (warm), and `getLaunchUrl()`
 * when the deep link *launched* the app from cold — both are handled so a
 * killed-app login doesn't hang with an unexchanged code.
 */
export function useMobileOktaHandoffListener(): void {
  const navigate = useNavigate();

  useEffect(() => {
    if (!IS_CAPACITOR) return;

    // Guards against navigating after the component unmounts (the exchange is
    // async and the deep link can fire during teardown).
    let active = true;
    // getLaunchUrl() and appUrlOpen can deliver the *same* cold-start callback
    // URL; dedupe so we don't run two exchanges for one code — the loser's
    // getdel would miss and route to the error UI after a real sign-in.
    const handledUrls = new Set<string>();

    // Close the system browser and route to the shared login-callback error UI
    // (visible message + retry) the way the web OAuth path does — a failed
    // handoff must not look like a hung app or leave the browser stranded. The
    // callback route owns the `login failed` tracking; `method` marks it mobile.
    const failLogin = (description: string): void => {
      void Browser.close().catch(() => undefined);

      if (!active) return;

      const params = new URLSearchParams({
        error: "mobile_handoff_failed",
        error_description: description,
        method: "okta_mobile",
      });
      navigate(`${navigationV2.loginCallback()}?${params.toString()}`, {
        replace: true,
      });
    };

    const handleCallbackUrl = (url: string): void => {
      if (!url.startsWith(MOBILE_OAUTH_CALLBACK_URL)) return;
      if (handledUrls.has(url)) return;
      handledUrls.add(url);

      const code = new URL(url).searchParams.get("mobile_handoff_code");

      // The callback fired for us but carries no code — e.g. an OAuth error
      // redirect from the server. Surface it and close the browser instead of
      // stranding the user on an error page outside the app.
      if (!code) {
        failLogin("Mobile sign-in did not complete");
        return;
      }

      void exchangeHandoffCode(code)
        .then(() => {
          void Browser.close().catch(() => undefined);
          if (!active) return;
          // Hand completion to the shared login-callback route (getMe +
          // destination resolution + `user logged in` analytics) instead of
          // jumping to a hardcoded page — otherwise successful mobile SSO
          // logins never emit the login events the web/email flows do. The
          // `method` marks this as the mobile flow.
          navigate(`${navigationV2.loginCallback()}?method=okta_mobile`, {
            replace: true,
          });
        })
        .catch((err: unknown) => {
          // The exchange failed (e.g. transient network error) — the code may
          // still be valid in Redis since getdel never ran, so drop it from the
          // dedup set to allow a re-delivered deep link to retry. The
          // synchronous add above still prevents concurrent double-exchange.
          handledUrls.delete(url);
          datadogLogs.logger.error("Mobile Okta handoff failed", {
            error: toErrorMessage(err),
          });
          failLogin(toErrorMessage(err, "Mobile sign-in failed"));
        });
    };

    const listener = CapacitorApp.addListener(
      "appUrlOpen",
      ({ url }: { url: string }) => handleCallbackUrl(url),
    );

    // Log (separately from the cleanup chain) if the warm-path listener never
    // registers, so a silent failure to catch the deep link is diagnosable.
    void listener.catch((err: unknown) =>
      datadogLogs.logger.error(
        "Mobile Okta appUrlOpen listener registration failed",
        { error: toErrorMessage(err) },
      ),
    );

    void CapacitorApp.getLaunchUrl()
      .then((launch) => {
        if (launch?.url) handleCallbackUrl(launch.url);
      })
      .catch((err: unknown) =>
        datadogLogs.logger.error("Mobile Okta getLaunchUrl failed", {
          error: toErrorMessage(err),
        }),
      );

    return () => {
      active = false;
      void listener.then((handle) => handle.remove()).catch(() => undefined);
    };
  }, [navigate]);
}
